import React, { useState, useMemo } from 'react';
import { voyageStats, voyageStatsFull, voyageDuration, voyageRoute, fmtDate, fmtHrs } from '../lib/utils';

const SANS = "'Plus Jakarta Sans',sans-serif";
const MONO = "'IBM Plex Mono',monospace";

const shortDate = (d) => (fmtDate(d) || '').replace(/^\w+,\s*/, '').replace(/,\s*(\d{4})$/, ', $1');
const dateRange = (v, stats) => {
  const s = v.start_date || stats.firstDate;
  const e = v.end_date;
  if (!s) return '';
  if (!e) return `${shortDate(s)} – present`;
  return `${shortDate(s)} – ${shortDate(e)}`;
};

// ── One card in the voyages list ──
function VoyageCard({ voyage, entries, onOpen }) {
  const stats = voyageStats(entries);
  const route = voyageRoute(entries);
  const inProgress = !voyage.end_date;
  return (
    <div onClick={() => onOpen(voyage)} style={{ background: 'var(--surface)', borderRadius: 16, overflow: 'hidden', marginBottom: 14, cursor: 'pointer', boxShadow: '0 1px 2px rgba(11,22,38,.04), 0 8px 24px rgba(11,22,38,.06)' }}>
      <div style={{ height: 150, position: 'relative', background: 'var(--s2)' }}>
        {voyage.cover_url
          ? <img src={voyage.cover_url} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--slate)', opacity: .4 }}>
              <svg viewBox="0 0 24 24" width="42" height="42" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M3 17l6-3 6 3 6-3V6l-6 3-6-3-6 3z" /></svg>
            </div>}
        <span style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(11,22,38,.82)', color: '#fff', fontFamily: MONO, fontSize: 9, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', padding: '5px 10px', borderRadius: 6 }}>{stats.entries} {stats.entries === 1 ? 'entry' : 'entries'}</span>
        {inProgress && <span style={{ position: 'absolute', top: 12, right: 12, background: 'var(--gold)', color: '#fff', fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', padding: '5px 10px', borderRadius: 6 }}>In progress</span>}
      </div>
      <div style={{ padding: '15px 17px 17px' }}>
        <div style={{ fontFamily: SANS, fontSize: 19, fontWeight: 600, color: 'var(--navy)', letterSpacing: '-.01em' }}>{voyage.name}</div>
        <div style={{ fontFamily: MONO, fontSize: 11, color: 'var(--slate)', marginTop: 3 }}>{dateRange(voyage, stats)}</div>
        {route && <div style={{ fontFamily: SANS, fontSize: 12.5, color: 'var(--slate)', marginTop: 5 }}>{route}</div>}
        <div style={{ display: 'flex', marginTop: 14 }}>
          {[[stats.distance.toLocaleString(), 'nm'], [stats.passages, 'Passages'], [stats.nights, 'Nights'], [fmtHrs(stats.seaHrs) || '0h', 'At sea']].map(([n, l], i) => (
            <div key={l} style={{ flex: 1, textAlign: 'center', borderLeft: i ? '1px solid var(--hair)' : 'none' }}>
              <div style={{ fontFamily: MONO, fontSize: 17, fontWeight: 600, color: 'var(--navy)' }}>{n}</div>
              <div style={{ fontFamily: SANS, fontSize: 9, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--slate)', marginTop: 3 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── The voyage list ──
export function VoyageList({ voyages, entriesByVoyage, onOpen, onNew }) {
  return (
    <div style={{ paddingBottom: 'calc(88px + env(safe-area-inset-bottom))' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0 4px 12px' }}>
        <button onClick={onNew} style={{ fontFamily: SANS, fontSize: 12, fontWeight: 600, padding: '9px 15px', borderRadius: 10, border: 'none', background: 'var(--navy)', color: '#fff', cursor: 'pointer' }}>+ New voyage</button>
      </div>
      {voyages.length === 0
        ? <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--slate)', fontSize: 14 }}>No voyages yet. Create one to group passages and stays into a trip.</div>
        : voyages.map(v => <VoyageCard key={v.id} voyage={v} entries={entriesByVoyage[v.id] || []} onOpen={onOpen} />)}
    </div>
  );
}

// ── Stats tab content (real-data-only, "est." on inferred) ──
function StatSection({ title, children }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 4px 10px' }}>
        <span style={{ fontFamily: SANS, fontSize: 12, fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--slate)' }}>{title}</span>
        <span style={{ flex: 1, height: 1, background: 'var(--hair)' }} />
      </div>
      <div style={{ background: 'var(--surface)', borderRadius: 16, padding: '16px 18px', marginBottom: 16, boxShadow: '0 1px 2px rgba(11,22,38,.04), 0 8px 24px rgba(11,22,38,.06)' }}>{children}</div>
    </>
  );
}
function Est() {
  return <span style={{ display: 'inline-block', fontFamily: MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--gold-deep, #8B6508)', background: 'rgba(184,134,11,.12)', padding: '2px 5px', borderRadius: 4, marginLeft: 6, verticalAlign: 'middle' }}>est</span>;
}
function Headline({ value, unit, navy }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
      <span style={{ fontFamily: MONO, fontSize: 30, fontWeight: 600, color: navy ? 'var(--navy)' : 'var(--gold)', letterSpacing: '-.01em' }}>{value}</span>
      <span style={{ fontFamily: SANS, fontSize: 12, fontWeight: 600, color: 'var(--slate)' }}>{unit}</span>
    </div>
  );
}
function Row({ k, leg, date, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '9px 0', borderTop: '1px solid var(--hair)' }} className="vrow">
      <div style={{ fontFamily: SANS, fontSize: 13, color: 'var(--slate)' }}>
        {k}
        {leg && <span style={{ display: 'block', fontFamily: MONO, fontSize: 10, color: 'var(--slate)', opacity: .7, marginTop: 2 }}>{leg}{date ? ` · ${shortDate(date)}` : ''}</span>}
      </div>
      <div style={{ fontFamily: MONO, fontSize: 15, fontWeight: 600, color: 'var(--navy)', textAlign: 'right' }}>{children}</div>
    </div>
  );
}

function VoyageStatsPanel({ entries }) {
  const s = voyageStatsFull(entries);
  const firstRow = { borderTop: 'none' };
  return (
    <div style={{ paddingBottom: 'calc(88px + env(safe-area-inset-bottom))' }}>
      <style>{`.vrow:first-of-type{border-top:none !important;}`}</style>

      {s.distance && (
        <StatSection title="Distance">
          <Headline value={s.distance.total.toLocaleString()} unit={`nm total · ${s.distance.passages} passages`} />
          {s.distance.longest && <Row k="Longest leg" leg={s.distance.longest.name} date={s.distance.longest.date}>{s.distance.longest.v} nm</Row>}
          {s.distance.shortest && <Row k="Shortest leg" leg={s.distance.shortest.name} date={s.distance.shortest.date}>{s.distance.shortest.v} nm</Row>}
          {s.distance.avg != null && <Row k="Average leg">{s.distance.avg} nm</Row>}
        </StatSection>
      )}

      {s.time && (
        <StatSection title="Time">
          <Headline navy value={s.time.total} unit={`at sea · ${s.time.daysUnderway + s.time.daysRest} days out`} />
          {s.time.longest && <Row k="Longest leg" leg={s.time.longest.name} date={s.time.longest.date}>{s.time.longest.v}</Row>}
          {s.time.shortest && <Row k="Shortest leg" leg={s.time.shortest.name} date={s.time.shortest.date}>{s.time.shortest.v}</Row>}
          {s.time.avg && <Row k="Average leg">{s.time.avg}</Row>}
          <Row k="Days underway / at rest">{s.time.daysUnderway} / {s.time.daysRest}</Row>
        </StatSection>
      )}

      {s.speed && (
        <StatSection title="Speed">
          {s.speed.avg != null && <Row k="Average SOG">{s.speed.avg} kts</Row>}
          {s.speed.top && <Row k="Top SOG" leg={s.speed.top.name} date={s.speed.top.date}>{s.speed.top.v} kts</Row>}
          {s.speed.bestDay != null && <Row k="Best day's run">{s.speed.bestDay} nm</Row>}
        </StatSection>
      )}

      {s.sailing && (
        <StatSection title="Sailing">
          <Headline value={<>{s.sailing.pct}<span style={{ fontSize: 18 }}>%</span></>} unit="under sail (distance-weighted)" />
          <Row k="Sailed / motored">{s.sailing.sailedNm} / {s.sailing.motoredNm} nm</Row>
        </StatSection>
      )}

      {s.nights && (
        <StatSection title="Nights & Stays">
          {s.nights.anchor > 0 && <Row k="Nights at anchor">{s.nights.anchor}</Row>}
          {s.nights.syc > 0 && <Row k="Nights at SYC">{s.nights.syc}</Row>}
          {s.nights.other > 0 && <Row k="Nights at other docks">{s.nights.other}</Row>}
          <Row k="Total nights aboard">{s.nights.total}</Row>
          <Row k="Stops">{s.nights.stops}</Row>
        </StatSection>
      )}

      {s.engine && (
        <StatSection title="Engine & Generator">
          {s.engine.engineRun != null && <Row k="Engine hours run">{s.engine.engineRun} h <Est /></Row>}
          {s.engine.genRun != null && <Row k="Generator hours run">{s.engine.genRun} h <Est /></Row>}
          {s.engine.engFirst != null && s.engine.engLast != null && <Row k="Engine start → latest">{s.engine.engFirst.toLocaleString()} → {s.engine.engLast.toLocaleString()}</Row>}
        </StatSection>
      )}

      {s.fuel && (
        <StatSection title="Fuel">
          <Row k="Estimated burned">{s.fuel.burned} gal <Est /></Row>
          <Row k="Avg consumption">{s.fuel.gph} gal/h <Est /></Row>
          {s.fuel.nmPerGal != null && <Row k="Efficiency">{s.fuel.nmPerGal} nm/gal <Est /></Row>}
        </StatSection>
      )}

      {!s.distance && !s.time && !s.nights && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--slate)', fontSize: 14 }}>No stats yet — add entries with distance, time, or stays to this voyage.</div>
      )}
    </div>
  );
}


export function VoyagePage({ voyage, entries, DetailCard, MapBlock, onBack, onEdit, onDelete, onOpenLeg }) {
  const stats = voyageStats(entries);
  const duration = voyageDuration(voyage, entries);
  const inProgress = !voyage.end_date;
  const legs = [...entries].sort((a, b) => new Date(a.date) - new Date(b.date));
  const [menu, setMenu] = useState(false);
  const [subtab, setSubtab] = useState('log');   // 'log' | 'stats'

  const cells = [
    [stats.distance.toLocaleString(), 'Hull nm', true],
    [fmtHrs(stats.seaHrs) || '0h', 'Time at sea'],
    [stats.passages, 'Passages'],
    [stats.anchored, 'Anchored'],
    [stats.docked, 'Docked'],
    [stats.stays, 'Stays'],
    [duration != null ? duration : '—', 'Days'],
    [stats.entries, 'Entries'],
  ];

  return (
    <div style={{ paddingBottom: 'calc(88px + env(safe-area-inset-bottom))' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 4px 12px' }}>
        <button onClick={onBack} style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: 'var(--slate)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>‹ Voyages</button>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setMenu(m => !m)} aria-label="Voyage menu" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--slate)', fontSize: 20, lineHeight: 1, padding: '2px 6px' }}>⋯</button>
          {menu && (
            <div style={{ position: 'absolute', right: 0, top: 28, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 8px 24px rgba(11,22,38,.16)', zIndex: 10, overflow: 'hidden', minWidth: 150 }}>
              <button onClick={() => { setMenu(false); onEdit(voyage); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 14px', background: 'none', border: 'none', fontFamily: SANS, fontSize: 13, color: 'var(--navy)', cursor: 'pointer' }}>Edit voyage</button>
              <button onClick={() => { setMenu(false); onDelete(voyage); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 14px', background: 'none', border: 'none', borderTop: '1px solid var(--hair)', fontFamily: SANS, fontSize: 13, color: 'var(--red)', cursor: 'pointer' }}>Delete voyage</button>
            </div>
          )}
        </div>
      </div>

      {/* Hero */}
      <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', height: 210, marginBottom: 16, background: 'var(--s2)' }}>
        {voyage.cover_url && <img src={voyage.cover_url} alt="" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(11,22,38,.78), rgba(11,22,38,.05) 55%)' }} />
        <div style={{ position: 'absolute', left: 18, right: 18, bottom: 16, color: '#fff' }}>
          <div style={{ fontFamily: MONO, fontSize: 9, fontWeight: 600, letterSpacing: '.16em', textTransform: 'uppercase', opacity: .85 }}>Voyage{inProgress ? ' · In progress' : ''}</div>
          <div style={{ fontFamily: SANS, fontSize: 26, fontWeight: 700, letterSpacing: '-.01em', marginTop: 3 }}>{voyage.name}</div>
          <div style={{ fontFamily: MONO, fontSize: 11, opacity: .9, marginTop: 2 }}>{dateRange(voyage, stats)}{voyageRoute(entries) ? ` · ${voyageRoute(entries)}` : ''}</div>
        </div>
      </div>

      {/* Log / Stats sub-tabs */}
      <div style={{ display: 'flex', background: 'var(--s2)', borderRadius: 11, padding: 4, gap: 4, margin: '0 4px 16px' }}>
        {[['log', 'Log'], ['stats', 'Stats']].map(([id, lbl]) => (
          <button key={id} onClick={() => setSubtab(id)} style={{ flex: 1, background: subtab === id ? 'var(--surface)' : 'none', border: 'none', borderRadius: 8, padding: '10px 4px', fontFamily: SANS, fontSize: 13, fontWeight: 600, color: subtab === id ? 'var(--navy)' : 'var(--slate)', cursor: 'pointer', boxShadow: subtab === id ? '0 1px 3px rgba(11,22,38,.1)' : 'none' }}>{lbl}</button>
        ))}
      </div>

      {subtab === 'stats'
        ? <VoyageStatsPanel entries={entries} />
        : <>
      {/* Whole-voyage animated map */}
      {MapBlock}

      {/* Full stat strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', background: 'var(--surface)', borderRadius: 16, padding: '16px 4px', marginBottom: 16, boxShadow: '0 1px 2px rgba(11,22,38,.04), 0 8px 24px rgba(11,22,38,.06)', rowGap: 16 }}>
        {cells.map(([n, l, gold], i) => (
          <div key={l} style={{ textAlign: 'center', borderLeft: (i % 4) ? '1px solid var(--hair)' : 'none' }}>
            <div style={{ fontFamily: MONO, fontSize: 18, fontWeight: 600, color: gold ? 'var(--gold)' : 'var(--navy)' }}>{n}</div>
            <div style={{ fontFamily: SANS, fontSize: 9, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--slate)', marginTop: 4 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Writeup */}
      {(voyage.writeup || '').trim() && (
        <>
          <div style={{ fontFamily: SANS, fontSize: 12, fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--slate)', margin: '4px 4px 10px' }}>The Voyage</div>
          <div style={{ background: 'var(--surface)', borderRadius: 16, padding: '18px 20px', marginBottom: 16, fontFamily: SANS, fontSize: 14.5, lineHeight: 1.7, color: 'var(--ink)', whiteSpace: 'pre-wrap', boxShadow: '0 1px 2px rgba(11,22,38,.04), 0 8px 24px rgba(11,22,38,.06)' }}>{voyage.writeup}</div>
        </>
      )}

      {/* Legs — existing posts in date order */}
      <div style={{ fontFamily: SANS, fontSize: 12, fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--slate)', margin: '4px 4px 10px' }}>Entries · in order</div>
      {legs.length === 0
        ? <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--slate)', fontSize: 13 }}>No entries assigned yet. Use Edit voyage to add them by date range.</div>
        : <div style={{ background: 'var(--surface)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 2px rgba(11,22,38,.04), 0 8px 24px rgba(11,22,38,.06)' }}>
            {legs.map((e, i) => <DetailCard key={e.id} entry={e} first={i === 0} onOpen={() => onOpenLeg(e)} />)}
          </div>}
        </>}
    </div>
  );
}
